import React, { useState, useEffect } from 'react';
import { userAPI, orgAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getInitials } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Plus,
  Search,
  Loader2,
  Mail,
  Building,
  Edit,
  Trash2,
  UserPlus,
  Shield,
  Clock,
} from 'lucide-react';

const ROLES = ['user', 'support', 'pm', 'developer', 'admin'];

const ROLE_DESCRIPTIONS = {
  user: 'Can view dashboard and submit tickets',
  support: 'Can manage tickets and view reports',
  pm: 'Can manage projects, tasks, and teams',
  developer: 'Can view and update assigned tasks',
  admin: 'Full access to all features',
};

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [search, setSearch] = useState('');
  const [filterOrg, setFilterOrg] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    organization: 'Intelvolks',
    department: '',
    role: 'user'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, orgsRes] = await Promise.all([
        userAPI.getUsers(),
        orgAPI.getOrganizations()
      ]);
      setUsers(usersRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async () => {
    setErrorMsg('');
    if (!formData.name || !formData.email || !formData.organization) return;
    if (!editingUser && !formData.password) {
      setErrorMsg('Please set an initial password for the user');
      return;
    }
    setSaving(true);
    try {
      if (editingUser) {
        await userAPI.updateUser(editingUser.id, formData);
      } else {
        await userAPI.createUser(formData);
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save user:', error);
      setErrorMsg(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setErrorMsg('');
    setFormData({
      name: user.name,
      email: user.email || '',
      password: '',
      organization: user.organization,
      department: user.department || '',
      role: user.role
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    try {
      await userAPI.deleteUser(userId);
      setDeleteConfirmUser(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const openInviteDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      organization: 'Intelvolks',
      department: '',
      role: 'user'
    });
  };

  const getDepartments = () => {
    const org = organizations.find(o => o.name === formData.organization || o.code === formData.organization);
    return org?.departments || ['Finance', 'Sales', 'IT', 'HR', 'Management'];
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(search.toLowerCase()) ||
                         (u.email && u.email.toLowerCase().includes(search.toLowerCase()));
    const matchesOrg = filterOrg === 'all' || u.organization === filterOrg;
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    return matchesSearch && matchesOrg && matchesRole && u.is_active !== false;
  });

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700';
      case 'pm': return 'bg-purple-100 text-purple-700';
      case 'support': return 'bg-blue-100 text-blue-700';
      case 'developer': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl font-black tracking-tight text-slate-900">
            Users
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Manage team members, assign roles, and invite new users
          </p>
        </div>

        <button
          onClick={openInviteDialog}
          className="flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium bg-[#002FA7] text-white hover:bg-[#002585] transition-colors"
          data-testid="add-user-btn"
        >
          <UserPlus className="h-4 w-4" />
          Invite User
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-lg p-4 bg-white border border-slate-200">
          <div className="text-2xl font-heading font-black text-slate-900">{users.filter(u => u.is_active !== false).length}</div>
          <div className="text-xs text-slate-500 mt-1">Total Active Users</div>
        </div>
        <div className="rounded-lg p-4 bg-white border border-slate-200">
          <div className="text-2xl font-heading font-black text-slate-900">{users.filter(u => u.role === 'admin').length}</div>
          <div className="text-xs text-slate-500 mt-1">Admins</div>
        </div>
        <div className="rounded-lg p-4 bg-white border border-slate-200">
          <div className="text-2xl font-heading font-black text-slate-900">{users.filter(u => u.role === 'pm').length}</div>
          <div className="text-xs text-slate-500 mt-1">Project Managers</div>
        </div>
        <div className="rounded-lg p-4 bg-white border border-slate-200">
          <div className="text-2xl font-heading font-black text-slate-900">{users.filter(u => u.role === 'developer').length}</div>
          <div className="text-xs text-slate-500 mt-1">Developers</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name, phone, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-md bg-white border-slate-200 text-slate-800"
            data-testid="search-users-input"
          />
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-32 rounded-md bg-white border-slate-200 text-slate-800" data-testid="filter-role-select">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {ROLES.map((role) => (
              <SelectItem key={role} value={role} className="capitalize">{role}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Users - Card layout on mobile, table on desktop */}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Department</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Activity</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-400">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#002FA7] flex items-center justify-center">
                          <span className="font-bold text-white text-sm">{getInitials(u.name)}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-slate-800">{u.name}</p>
                          <p className="text-xs text-slate-400">ID: {u.id.slice(0, 8)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {u.email && (
                          <p className="flex items-center gap-1.5 text-sm text-slate-600">
                            <Mail className="h-3 w-3 text-slate-400" /> {u.email}
                          </p>
                        )}
                        {!u.email && <span className="text-xs text-slate-300">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{u.department || '-'}</td>
                    <td className="px-4 py-3">
                      <Badge className={`rounded-full capitalize text-xs ${getRoleBadgeColor(u.role)}`}>
                        {u.role}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3 w-3 text-slate-400 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-slate-600">
                            {u.last_activity ? new Date(u.last_activity).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                          </p>
                          {u.last_action && <p className="text-[10px] text-slate-400 capitalize">{u.last_action}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(u)}
                          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                          data-testid={`edit-user-${u.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {u.id !== currentUser?.id && !u.is_protected && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmUser(u); }}
                            className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                            data-testid={`delete-user-${u.id}`}
                            title="Delete user"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card Layout */}
        <div className="md:hidden divide-y divide-slate-100">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-slate-400">No users found</div>
          ) : (
            filteredUsers.map((u) => (
              <div key={u.id} className="p-4" data-testid={`user-card-${u.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-[#002FA7] flex items-center justify-center flex-shrink-0">
                      <span className="font-bold text-white text-sm">{getInitials(u.name)}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm text-slate-800 truncate">{u.name}</p>
                      {u.email && (
                        <p className="text-xs text-slate-400 truncate">{u.email}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleEdit(u)}
                      className="p-2 rounded-md bg-slate-50 text-slate-500 active:bg-slate-100 transition-colors"
                      data-testid={`edit-user-mobile-${u.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    {u.id !== currentUser?.id && !u.is_protected && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirmUser(u); }}
                        className="p-2 rounded-md bg-red-50 text-red-400 active:bg-red-100 transition-colors"
                        data-testid={`delete-user-mobile-${u.id}`}
                        title="Delete user"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2.5 ml-[52px]">
                  <Badge className={`rounded-full capitalize text-[10px] ${getRoleBadgeColor(u.role)}`}>
                    {u.role}
                  </Badge>
                  {u.department && (
                    <span className="text-[10px] text-slate-400">{u.department}</span>
                  )}
                  {u.last_activity && (
                    <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                      <Clock className="h-2.5 w-2.5" />
                      {new Date(u.last_activity).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create/Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto border border-slate-200 bg-white text-slate-800" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold text-slate-800">
              {editingUser ? 'Edit User' : 'Invite New User'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 rounded-md bg-white border-slate-200 text-slate-800"
                  placeholder="Full name"
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email *</label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="mt-1 rounded-md bg-white border-slate-200 text-slate-800"
                  placeholder="email@company.com"
                  type="email"
                  data-testid="user-email-input"
                />
              </div>
            </div>
            {!editingUser && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Initial Password *</label>
                <Input
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="mt-1 rounded-md bg-white border-slate-200 text-slate-800"
                  placeholder="Min 6 characters"
                  type="password"
                  data-testid="user-password-input"
                />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Organization *</label>
                <Select value={formData.organization} onValueChange={(v) => setFormData(prev => ({ ...prev, organization: v, department: '' }))}>
                  <SelectTrigger className="mt-1 rounded-md bg-white border-slate-200 text-slate-800" data-testid="user-org-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.name}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Department</label>
                <Select value={formData.department} onValueChange={(v) => setFormData(prev => ({ ...prev, department: v }))}>
                  <SelectTrigger className="mt-1 rounded-md bg-white border-slate-200 text-slate-800" data-testid="user-dept-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {getDepartments().map((dept) => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Role Selection */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5" /> Role *
              </label>
              <div className="mt-2 grid grid-cols-1 gap-2">
                {ROLES.map((role) => (
                  <label
                    key={role}
                    className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                      formData.role === role
                        ? 'border-[#002FA7] bg-[#eef2ff]'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={role}
                      checked={formData.role === role}
                      onChange={() => setFormData(prev => ({ ...prev, role }))}
                      className="accent-[#002FA7]"
                    />
                    <div>
                      <span className="text-sm font-semibold capitalize text-slate-800">{role}</span>
                      <p className="text-xs text-slate-400">{ROLE_DESCRIPTIONS[role]}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {!editingUser && (
              <div className="p-3 rounded-md bg-blue-50 border border-blue-100">
                <p className="text-xs text-blue-700">
                  The user will sign in with their email and password. They can also use Google SSO if their email is a Google account.
                </p>
              </div>
            )}

            {errorMsg && (
              <div className="p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-600 font-medium" data-testid="user-form-error">
                {errorMsg}
              </div>
            )}
            <button
              onClick={handleSubmit}
              disabled={saving || !formData.name || !formData.email || !formData.organization}
              className="w-full py-2.5 rounded-md text-sm font-semibold bg-[#002FA7] text-white hover:bg-[#002585] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="save-user-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editingUser ? 'Update User' : 'Create & Invite User'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmUser && (
        <Dialog open={true} onOpenChange={() => setDeleteConfirmUser(null)}>
          <DialogContent className="max-sm:top-[50%] max-sm:left-[50%] max-sm:translate-x-[-50%] max-sm:translate-y-[-50%] max-sm:w-[calc(100%-2rem)] max-sm:h-auto max-sm:max-w-sm max-sm:rounded-xl sm:max-w-[400px]" data-testid="delete-user-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-slate-800">Delete User</DialogTitle>
            </DialogHeader>
            <div className="py-2">
              <p className="text-sm text-slate-600">
                Are you sure you want to deactivate <strong>{deleteConfirmUser?.name}</strong>?
              </p>
              <p className="text-xs text-slate-400 mt-1">They will no longer be able to log in.</p>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={() => setDeleteConfirmUser(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmUser?.id)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600"
                data-testid="confirm-delete-btn"
              >
                Delete
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Users;
